import PersonIcon from "@mui/icons-material/Person";
import { normalizeEmployeeCalendarColor } from "../../../staff/employee/addemployee/employeeFormShared";
import {
  calendarSlotFill,
  formatAppointmentPurpose,
  formatPatientTypeLabel,
  isLightCssColor,
  patientMobileFromItem,
  patientTypeLabelFromItem,
  PATIENT_TYPE_STYLES,
  purposeColorBackground,
  purposeColorFromItem,
} from "../../viewappointment/components/appointmentViewShared";

const CALENDAR_TEXT = "#334155";
const CALENDAR_MUTED = "#64748b";
/** Light indigo when purpose and doctor colour are both missing. */
const DEFAULT_ACCENT = "#c7d2fe";

/** Soft pink for completed calendar slots. */
const COMPLETED_SLOT_BG = "#fce7f3";
const COMPLETED_SLOT_BORDER = "#f9a8d4";
const COMPLETED_SLOT_TEXT = "#9d174d";

function calendarEventColors(
  appointment: any,
  colorByPurpose?: boolean,
  isCompleted?: boolean
) {
  if (isCompleted) {
    return {
      bg: COMPLETED_SLOT_BG,
      border: COMPLETED_SLOT_BORDER,
      accent: COMPLETED_SLOT_TEXT,
      text: COMPLETED_SLOT_TEXT,
      muted: COMPLETED_SLOT_TEXT,
    };
  }

  const purposeColor = purposeColorFromItem(appointment);
  const doctorColor =
    normalizeEmployeeCalendarColor(appointment?.color) || appointment?.color;
  const accent =
    colorByPurpose && purposeColor
      ? purposeColor
      : doctorColor || DEFAULT_ACCENT;
  const light = isLightCssColor(accent);

  return {
    bg: calendarSlotFill(accent, 22),
    border: purposeColorBackground(accent, light ? 70 : 48),
    accent: light ? CALENDAR_TEXT : accent,
    text: CALENDAR_TEXT,
    muted: CALENDAR_MUTED,
  };
}

export default function AppointmentEvent({
  appointment,
  isMonthView,
  isDayView,
  colorByPurpose,
  reschedule,
  onOnlineAppointmentClick,
}: {
  reschedule?: any;
  appointment: any;
  isMonthView?: boolean;
  isDayView?: boolean;
  /** When a doctor is selected, color slots by purpose instead of doctor color. */
  colorByPurpose?: boolean;
  onOnlineAppointmentClick?: any;
}) {
  const isCompleted = appointment?.status === "completed";
  const { bg, border, accent, text, muted } = calendarEventColors(
    appointment,
    colorByPurpose,
    isCompleted
  );
  const isDefault = appointment?.type === "default" && appointment?.type;
  const isOnline = appointment?.type === "online";
  const purpose = formatAppointmentPurpose(appointment);
  const showPurpose = !isMonthView && purpose && purpose !== "N/A";
  const phone = patientMobileFromItem(appointment);
  const showPhone = Boolean(phone && phone !== "—");
  const patientTypeLabel = patientTypeLabelFromItem(appointment);
  const patientTypeShort = formatPatientTypeLabel(patientTypeLabel);
  const showDoctor = !isMonthView && Boolean(appointment?.dentist);
  const overlapCount = Number(appointment?._overlapCount || 0);
  const showOverlapBadge = isDayView && overlapCount > 0;

  const metaParts = [
    showPhone ? phone : null,
    showPurpose ? purpose : null,
    showDoctor ? appointment?.dentist : null,
  ].filter(Boolean) as string[];

  const handleClick = () => {
    if (isOnline && !!onOnlineAppointmentClick && !appointment?.added) {
      onOnlineAppointmentClick(appointment);
    } else {
      reschedule(appointment);
    }
  };

  const title = [
    appointment?.patientName,
    showPhone ? phone : null,
    patientTypeShort,
    showPurpose ? purpose : null,
    appointment?.dentist,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      onClick={handleClick}
      className="h-full w-full px-1.5 py-0.5 rounded-lg cursor-pointer transition-all hover:shadow-md overflow-hidden"
      style={{
        backgroundColor: bg,
        color: text,
        border: isDefault ? "none" : `1px solid ${border}`,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
      }}
      title={title}
    >
      {isDayView && !isMonthView ? (
        <div className="flex items-center gap-1.5 h-full min-h-0 min-w-0 leading-tight">
          <PersonIcon
            sx={{ fontSize: 11, color: accent }}
            className="shrink-0 opacity-90"
          />
          <span
            className="text-[11px] font-semibold truncate shrink-0 max-w-[40%]"
            style={{ color: text }}
          >
            {appointment?.patientName}
          </span>
          <span
            className={`inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide border shrink-0 leading-none ${
              PATIENT_TYPE_STYLES[patientTypeLabel] ||
              PATIENT_TYPE_STYLES["Existing Patient"]
            }`}
          >
            {patientTypeShort}
          </span>
          {metaParts.length > 0 ? (
            <span
              className="min-w-0 flex-1 truncate text-[9px] font-medium"
              style={{ color: muted }}
            >
              {metaParts.join(" · ")}
            </span>
          ) : null}
          {showOverlapBadge ? (
            <span
              className="inline-flex shrink-0 items-center rounded-full border border-slate-300/70 bg-white/90 px-1.5 py-px text-[9px] font-semibold leading-none text-slate-600"
              title={`Overlaps with ${overlapCount} more appointment${
                overlapCount === 1 ? "" : "s"
              }`}
            >
              +{overlapCount}
            </span>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col justify-center gap-0 h-full min-h-0 leading-tight">
          <div className="flex items-center gap-1 min-w-0 shrink-0">
            <PersonIcon
              sx={{ fontSize: 11, color: accent }}
              className="shrink-0 opacity-90"
            />
            <span
              className="text-[11px] font-semibold truncate min-w-0 flex-1"
              style={{ color: text }}
            >
              {appointment?.patientName}
            </span>
            {!isMonthView ? (
              <span
                className={`inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide border shrink-0 leading-none ${
                  PATIENT_TYPE_STYLES[patientTypeLabel] ||
                  PATIENT_TYPE_STYLES["Existing Patient"]
                }`}
              >
                {patientTypeShort}
              </span>
            ) : null}
          </div>

          {!isMonthView && metaParts.length > 0 ? (
            <div
              className="min-w-0 truncate text-[9px] font-medium"
              style={{ color: muted }}
            >
              {metaParts.join(" · ")}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
